import axios from "axios";

const apiKey = process.env.REACT_APP_X_API_KEY;
const appKey = process.env.REACT_APP_APP_KEY;
const appBaseUrl = process.env.REACT_APP_API_ADMIN_BASE_URL;

// Helper function to get authentication headers based on user type
const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    const userType = localStorage.getItem("userType");
    const currentPartnerId = localStorage.getItem("pid");
    
    // If user has a Bearer token, use it
    if (token) {
        const headers = {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        };
        
        // Also include partner ID if available
        if (currentPartnerId) {
            headers["X-Partner-ID"] = currentPartnerId;
        }
        
        return headers;
    }
    
    // If user is a partner and has app key, use partner authentication
    if (userType === "partner" && appKey && currentPartnerId) {
        return {
            "APP-KEY": appKey,
            "X-Partner-ID": currentPartnerId,
            "Content-Type": "application/json"
        };
    }
    
    // Fallback to X-API-KEY for legacy support
    return {
        "X-API-KEY": apiKey,
        "Content-Type": "application/json"
    };
};

const analyticsApi = axios.create({
  baseURL: appBaseUrl,
  withCredentials: true,
});

// Export the variables to ensure they're accessible
export { apiKey, analyticsApi };


// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map();

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
  });
};

export const getDashboardAnalytics = async () => {
  const cacheKey = "dashboard_analytics";
  const cached = getCachedData(cacheKey);
  if (cached) return cached;

  try {
    const response = await axios.get(`${appBaseUrl}/analytics/dashboard`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    const data = response.data;
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    throw new Error(`Failed to fetch dashboard analytics: ${error.response?.status} ${error.response?.statusText}`);
  }
};

export const getPartnerAnalytics = async (partnerId) => {
  const cacheKey = `partner_analytics_${partnerId}`;
  const cached = getCachedData(cacheKey);
  if (cached) return cached;

  try {
    const response = await axios.get(`${appBaseUrl}/analytics/partner/${partnerId}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    const data = response.data;
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    throw new Error(`Failed to fetch partner analytics: ${error.response?.status} ${error.response?.statusText}`);
  }
};

export const getTransactionAnalytics = async (period = 30) => {
  const cacheKey = `transaction_analytics_${period}`;
  const cached = getCachedData(cacheKey);
  if (cached) return cached;

  try {
    const response = await axios.get(`${appBaseUrl}/analytics/transactions?period=${period}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    const data = response.data;
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    throw new Error("Failed to fetch transaction analytics");
  }
};

// Charges Analytics API Functions
export const getChargesSummary = async (startDate = null, endDate = null, partnerId = null, page = 1, perPage = 50) => {
  const cacheKey = `charges_summary_${startDate || 'default'}_${endDate || 'default'}_${partnerId || 'all'}_${page}_${perPage}`;
  const cached = getCachedData(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    let url = "/transactions/charges";
    const params = new URLSearchParams();
    
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (partnerId) params.append("partner_id", partnerId);
    params.append("page", page.toString());
    params.append("per_page", perPage.toString());
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    
    const response = await axios.get(`${appBaseUrl}${url}`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    const responseData = response.data;
    
    
    // Handle the documented response structure: { success: true, data: { ... } }
    const data = responseData.success ? responseData.data : responseData;
    
    
    if (data?.detailed_charges) {
    }
    
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    throw new Error(`Failed to fetch charges summary: ${error.response?.status} ${error.response?.statusText}`);
  }
};

export const exportChargesData = async (startDate = null, endDate = null, partnerId = null, page = 1, perPage = 1000) => {
  try {
    let url = "/export-charges";
    const params = new URLSearchParams();
    
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (partnerId) params.append("partner_id", partnerId);
    params.append("page", page.toString());
    params.append("per_page", perPage.toString());
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await axios.get(`${appBaseUrl}${url}`, {
      headers: getAuthHeaders(),
      withCredentials: true,
      responseType: 'blob'
    });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `charges_export_${startDate || 'all'}_to_${endDate || 'all'}.xlsx`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    // Create download link for Excel file
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url2 = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url2;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url2);
    
    return { success: true };
  } catch (error) {
    throw new Error(`Failed to export charges data: ${error.response?.status} ${error.response?.statusText}`);
  }
};

export const clearAnalyticsCache = () => {
  cache.clear();
};

export const clearSpecificCache = (key) => {
  cache.delete(key);
};

// Account Status API Function
export const getAccountStatus = async (orderId) => {
  if (!orderId) {
    throw new Error("Order ID is required");
  }

  try {
    // Use the correct base URL for transactions
    const transactionBaseUrl = process.env.REACT_APP_API_TRANSACTION_BASE_URL || 'http://localhost:8000/api/transactions';
    const fullUrl = `${transactionBaseUrl}/account-status?orderId=${orderId}`;
    
    
    // This is a PUBLIC endpoint that requires NO headers according to the API reference
    const response = await axios.get(fullUrl, {
      withCredentials: true
    });
    
    
    // Log specific fields from the response
    if (response.data) {
      
      if (response.data.data) {
      }
    }
    
    // Return the response data with proper status handling
    if (response.data.success) {
      const result = {
        ...response.data,
        status: response.data.data?.status || 'success'
      };
      return result;
    } else {
      // Handle case where API returns success: false but with data
      const result = {
        ...response.data,
        status: response.data.data?.status || response.data.status || 'unknown'
      };
      return result;
    }
  } catch (error) {
    // Handle specific error responses and return structured data
    if (error.response?.status === 404) {
      const notFoundResult = {
        success: false,
        message: "OrderId not found",
        orderId: orderId,
        status_code: 404,
        data: {
          data: {
            amount: "N/A",
            merchantId: "N/A",
            payer_account_name: "N/A",
            payer_bank: "N/A",
            paid_at: "N/A",
            details: "Order ID not found in the system"
          }
        },
        status: "not_found"
      };
      return notFoundResult;
    }
    
    if (error.response?.status === 400) {
      const badRequestResult = {
        success: false,
        message: "Failed",
        orderId: orderId,
        status_code: 400,
        data: {
          data: {
            amount: "N/A",
            merchantId: "N/A",
            payer_account_name: "N/A",
            payer_bank: "N/A",
            paid_at: "N/A",
            details: "Transaction failed or invalid request"
          }
        },
        status: "failed"
      };
      return badRequestResult;
    }

    if (error.response?.status === 500) {
      const serverErrorResult = {
        success: false,
        message: "Server error occurred",
        orderId: orderId,
        status_code: 500,
        data: {
          data: {
            amount: "N/A",
            merchantId: "N/A",
            payer_account_name: "N/A",
            payer_bank: "N/A",
            paid_at: "N/A",
            details: "Server error occurred while processing request"
          }
        },
        status: "server_error"
      };
      return serverErrorResult;
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      const unauthorizedResult = {
        success: false,
        message: "Unauthorized access",
        orderId: orderId,
        status_code: error.response.status,
        data: {
          data: {
            amount: "N/A",
            merchantId: "N/A",
            payer_account_name: "N/A",
            payer_bank: "N/A",
            paid_at: "N/A",
            details: "Access denied or authentication required"
          }
        },
        status: "unauthorized"
      };
      return unauthorizedResult;
    }
    
    // For other errors, throw the original error
    throw new Error(`Failed to fetch account status: ${error.response?.status} ${error.response?.statusText}`);
  }
};

// Settlement Status API Function
export const checkSettlementStatus = async (searchParams) => {
  const { transactionReference, providerPaymentId } = searchParams;
  
  if (!transactionReference && !providerPaymentId) {
    throw new Error("Either transaction reference or provider payment ID is required");
  }

  try {
    const requestBody = {};
    if (transactionReference) requestBody.transactionReference = transactionReference;
    if (providerPaymentId) requestBody.provider_payment_id = providerPaymentId;

    // Use the correct base URL for transactions
    const transactionBaseUrl = process.env.REACT_APP_API_TRANSACTION_BASE_URL || 'http://localhost:8000/api/transactions';
    const fullUrl = `${transactionBaseUrl}/settlement-status-db`;


    const response = await axios.post(fullUrl, requestBody, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    // Handle specific error responses and return structured data
    if (error.response?.status === 404) {
      const notFoundResult = {
        status: false,
        message: "Transaction not found",
        transactionId: transactionReference || providerPaymentId,
        status_code: 404,
        data: {
          status: "not_found",
          amount: "N/A",
          merchantId: "N/A",
          payer_account_name: "N/A",
          payer_bank: "N/A",
          paid_at: "N/A",
          details: "Transaction not found in the system"
        }
      };
      return notFoundResult;
    }
    
    if (error.response?.status === 400) {
      const badRequestResult = {
        status: false,
        message: "Invalid request",
        transactionId: transactionReference || providerPaymentId,
        status_code: 400,
        data: {
          status: "failed",
          amount: "N/A",
          merchantId: "N/A",
          payer_account_name: "N/A",
          payer_bank: "N/A",
          paid_at: "N/A",
          details: "Invalid transaction reference or provider payment ID"
        }
      };
      return badRequestResult;
    }

    if (error.response?.status === 500) {
      const serverErrorResult = {
        status: false,
        message: "Server error occurred",
        transactionId: transactionReference || providerPaymentId,
        status_code: 500,
        data: {
          status: "server_error",
          amount: "N/A",
          merchantId: "N/A",
          payer_account_name: "N/A",
          payer_bank: "N/A",
          paid_at: "N/A",
          details: "Server error occurred while processing request"
        }
      };
      return serverErrorResult;
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      const unauthorizedResult = {
        status: false,
        message: "Unauthorized access",
        transactionId: transactionReference || providerPaymentId,
        status_code: error.response.status,
        data: {
          status: "unauthorized",
          amount: "N/A",
          merchantId: "N/A",
          payer_account_name: "N/A",
          payer_bank: "N/A",
          paid_at: "N/A",
          details: "Access denied or authentication required"
        }
      };
      return unauthorizedResult;
    }
    
    // For other errors, throw the original error
    throw new Error(`Failed to check settlement status: ${error.response?.status} ${error.response?.statusText}`);
  }
};

// Health check function to test API connectivity
export const testApiConnection = async () => {
  try {
    const response = await axios.get(`${appBaseUrl}/health`, {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}; 